import { useCallback, useRef } from "react";
import useAssetView from "domains/assets/hooks/useAssetView";
import { ASSET_TYPE } from "domains/assets/utils/getIsType";
import FileManagerImage, {
  FileManagerImageProps,
} from "domains/file-manager/components/FileManagerImage";
import Icon from "domains/ui/components/Icon";

import { Text, VStack } from "@chakra-ui/react";

export type ExploreContentProps<C extends object = object> = Pick<
  FileManagerImageProps<object, C, object>,
  | "scrollRef"
  | "EmptyStateComponent"
  | "files"
  | "isLoading"
  | "hasMore"
  | "onEndReached"
  | "isSkeleton"
  | "headerSortProps"
  | "CardActionsComponent"
  | "cardActionsProps"
  | "onVary"
  | keyof ReturnType<typeof useAssetView>
> & {
  value: string | undefined;
  lastScrollTop: number;
  imagesCount: number;
  isLoadingImages: boolean;
  isEmpty: boolean;
  type: "image" | "skybox" | "texture" | "video" | "3d";
  onFirstRender: () => void;
};

export default function ExploreContent<C extends object = object>({
  type,
  lastScrollTop,
  imagesCount,
  isLoadingImages = false,
  isEmpty = false,
  onVary,
  onFirstRender,
  ...props
}: ExploreContentProps<C>) {
  const isRendered = useRef(false);
  const displayedImagesCount =
    (imagesCount && imagesCount > 100 && "100+") ||
    (!isLoadingImages && !imagesCount && "No") ||
    imagesCount;
  const assetType = ASSET_TYPE[type][imagesCount > 1 ? "plural" : "singular"];

  const handleRender = useCallback(() => {
    if (isRendered.current) return;
    onFirstRender();
    setTimeout(() => {
      props.scrollRef?.current?.scrollTo({
        top: lastScrollTop,
        behavior: "smooth",
      });
    }, 500);
    isRendered.current = true;
  }, [onFirstRender, lastScrollTop, props.scrollRef]);

  return (
    <FileManagerImage
      {...props}
      variant={type}
      isSelectable
      minColumnsCount={3}
      EmptyStateComponent={props.EmptyStateComponent ?? EmptyMessage}
      headerLeftContent={
        <Text size="body.bold.lg">
          {!imagesCount ? "" : `${displayedImagesCount} ${assetType}`}
        </Text>
      }
      isVaryEnabled
      onVary={onVary}
      onRender={handleRender}
      styleProps={{
        header: {
          top: 0,
          pt: isEmpty ? 0 : 6,
          pos: "relative",
          zIndex: "unset",
        },
      }}
    />
  );
}

// ------------------------------------

function EmptyMessage() {
  return (
    <VStack justify="center" py={20} spacing={2}>
      <Icon id="Ui/Search" h="16px" color="textPrimary" />
      <Text color="textPrimary" size="body.md">
        Sorry - no results found
      </Text>
      <Text color="textTertiary" size="body.sm">
        Click on another category to keep exploring
      </Text>
    </VStack>
  );
}
